import {
  ButtonMediumPrimary,
  ButtonMediumSecondary,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import type React from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListPageHeader } from "@/components/ListPageHeader";
import { useToast } from "@/components/toast/ToastProvider";
import { useErrorToast } from "@/components/toast/useErrorToast";
import type { CreateServiceRequest } from "@/types/api";
import { SelectDropdown } from "./SelectDropdown";
import {
  BareInput,
  Card,
  CommonFields,
  FieldRow,
  FieldRowControl,
  Form,
  HeaderActions,
  HelpText,
  IconInputBox,
  INPUT_LEADING_ICON_SIZE,
  Input,
  Label,
  Page,
} from "./styles";
import { useCreateService } from "./useCreateService";
import {
  PROVIDER_OPTIONS,
  type ProviderChoice,
  useProviderFields,
} from "./useProviderFields";

// The service name row above the card. A UDF service's name is also its
// scripts folder (udfScriptPath), which is how Figma 14058-10184 labels it:
// "Folder name" with a folder icon leading the input.
function NameField({
  id,
  value,
  onChange,
  provider,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  provider: ProviderChoice;
}) {
  const inputProps = {
    id,
    type: "text",
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.value),
    pattern: "[a-zA-Z0-9_\\-]+",
    required: true,
  };
  const isFolder = provider === "udf";

  return (
    <FieldRow>
      {/* Figma 13017-8763 says "Services name" — copy typo, kept singular. */}
      <Label htmlFor={id}>{isFolder ? "Folder name*" : "Service name*"}</Label>
      <FieldRowControl>
        {isFolder ? (
          <IconInputBox>
            <IconSVG
              icon={Icon.Folder}
              size={INPUT_LEADING_ICON_SIZE}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
            <BareInput
              {...inputProps}
              placeholder="Add a name to the folder which contains the scripts"
            />
          </IconInputBox>
        ) : (
          <Input {...inputProps} placeholder="Add a name to the service" />
        )}
        <HelpText>
          Alphanumeric characters, hyphens, and underscores only
          {isFolder && "."}
        </HelpText>
      </FieldRowControl>
    </FieldRow>
  );
}

function AddService() {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const nameId = useId();

  const [provider, setProvider] = useState<ProviderChoice>("mongodb");
  const [name, setName] = useState("");

  const activeHook = useProviderFields(provider, name);

  const buildRequest = useCallback(
    (): CreateServiceRequest => ({
      provider: activeHook.provider,
      name,
      ...activeHook.toPayload(),
    }),
    [name, activeHook],
  );

  const { loading, error, success, handleSubmit, clearError } =
    useCreateService(buildRequest, activeHook.afterCreate);

  // Submit feedback is a toast (the bottom-right stack), not a banner on
  // the page: a failure stays until closed, the success note leaves on its
  // own ahead of the redirect.
  useErrorToast(error, clearError);
  const { toast } = useToast();
  useEffect(() => {
    if (success) {
      toast({
        state: "Confirm",
        message: "Service created successfully! Redirecting...",
      });
    }
  }, [success, toast]);

  const handleCancel = useCallback(() => {
    navigate("/services");
  }, [navigate]);

  // The header's Add button lives outside the <form> (and the design-system
  // buttons render type="button"), so route it through requestSubmit to keep
  // native validation and the onSubmit path.
  const requestSubmit = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  return (
    <Page>
      {/* Shared sticky page header, same as the single-service page: the
          title row stays pinned while the form scrolls. */}
      <ListPageHeader
        title="New service"
        action={
          <HeaderActions>
            <ButtonMediumSecondary
              text="Cancel"
              onClick={handleCancel}
              disabled={loading}
            />
            <ButtonMediumPrimary
              text="Add"
              onClick={requestSubmit}
              disabled={loading}
              loading={loading}
            />
          </HeaderActions>
        }
      />

      <Form ref={formRef} onSubmit={handleSubmit}>
        {/* Shared across all providers: name + provider sit above the card
            and overhang its top border (Figma 13017-8763). */}
        <CommonFields>
          <NameField
            id={nameId}
            value={name}
            onChange={setName}
            provider={provider}
          />

          <FieldRow>
            <Label as="span">Provider</Label>
            <FieldRowControl>
              <SelectDropdown
                value={provider}
                options={PROVIDER_OPTIONS}
                onChange={setProvider}
              />
            </FieldRowControl>
          </FieldRow>
        </CommonFields>

        <Card>{activeHook.fields}</Card>
      </Form>
    </Page>
  );
}

export default AddService;
